import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Table, Card, Button, Space, Tag, Typography, Row, Grid, Modal } from 'antd';
import { PlusOutlined, SyncOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getSortState, JhiPagination, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { createEntity, getEntities, updateEntity } from './matiere-ecole.reducer';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { getEcole } from '../ecole/ecole.reducer';
import { IMatiereEcole } from 'app/shared/model/matiere.model';

const { Title } = Typography;
const { useBreakpoint } = Grid;

export const MatiereEcole = (props: RouteComponentProps<{ ecoleid: string }>) => {
  const dispatch = useAppDispatch();
  const matiereList = useAppSelector(state => state.matiereEcole.entities);
  const totalItems = useAppSelector(state => state.matiereEcole.totalItems);
  const loading = useAppSelector(state => state.matiereEcole.loading);
  const updating = useAppSelector(state => state.matiereEcole.updating);
  const screens = useBreakpoint();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedMatiere, setSelectedMatiere] = useState<IMatiereEcole | null>(null);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(
      getEntities({
        ecoleId: props.match.params.ecoleid,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
      })
    );
  }, [paginationState]);

  const handleSyncList = () => {
    dispatch(
      getEntities({
        ecoleId: props.match.params.ecoleid,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
      })
    );
  };

  const handlePagination = (currentPage: number) => {
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });
  };

  const navigate = (pathname: string) => props.history.push(pathname);

  const closeFormModal = () => {
    if (!updating) {
      setFormModalOpen(false);
      setSelectedMatiere(null);
    }
  };

  const handleSave = async values => {
    const entity = {
      ...selectedMatiere,
      ...values,
      ecoleId: props.match.params.ecoleid,
    };
    const action = selectedMatiere?.id ? updateEntity : createEntity;
    const result = await dispatch(action(entity));
    if (action.fulfilled.match(result)) {
      setFormModalOpen(false);
      setSelectedMatiere(null);
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
    },
    {
      title: 'Couleur',
      dataIndex: 'color',
      key: 'color',
      render: color => (
        <Tag color={color} style={{ width: 70, textAlign: 'center' }}>
          {color}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: screens.xs ? 100 : 200,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedMatiere(record);
              setFormModalOpen(true);
            }}
            size={screens.xs ? 'small' : 'middle'}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => navigate(`${props.match.url}/${record.id}/delete`)}
            size={screens.xs ? 'small' : 'middle'}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px 24px' }} bodyStyle={{ padding: screens.xs ? '0 8px 16px' : '0 24px 24px' }}>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '1.5rem' : '2rem' }}>
          <Translate contentKey="korischoolApp.matiere.home.title">Matieres</Translate>
        </Title>
        <Space>
          <Button onClick={handleSyncList} className="d-flex align-items-center" disabled={loading} size={screens.xs ? 'small' : 'middle'}>
            <SyncOutlined spin={loading} />
            <Translate contentKey="korischoolApp.matiere.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            className="d-flex align-items-center justify-content-between gap-3 rounded"
            onClick={() => {
              setSelectedMatiere(null);
              setFormModalOpen(true);
            }}
            icon={<PlusOutlined />}
            style={{
              backgroundColor: '#F28E19',
              color: '#fff',
              fontWeight: 'bold',
              padding: screens.xs ? '6px 8px' : '8px 16px',
              fontSize: screens.xs ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Translate contentKey="korischoolApp.matiere.home.createLabel">Create new Matiere</Translate>
          </Button>
        </Space>
      </div>

      <Table
        dataSource={matiereList}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={loading}
        locale={{
          emptyText: <Translate contentKey="korischoolApp.matiere.home.notFound">No Matieres found</Translate>,
        }}
        bordered
        size="middle"
        style={{ backgroundColor: 'white' }}
        scroll={{ x: screens.xs ? 'true' : 'false' }}
      />

      {matiereList && matiereList.length > 0 && (
        <Row className="justify-content-center" style={{ marginTop: '16px' }}>
          <JhiPagination
            activePage={paginationState.activePage}
            onSelect={handlePagination}
            maxButtons={5}
            itemsPerPage={paginationState.itemsPerPage}
            totalItems={totalItems}
          />
        </Row>
      )}

      <Modal
        title={selectedMatiere ? 'Modifier la matière' : 'Créer une matière'}
        visible={formModalOpen}
        onCancel={closeFormModal}
        footer={null}
        destroyOnClose
        maskClosable={!updating}
        closable={!updating}
      >
        <ValidatedForm key={selectedMatiere?.id || 'create'} defaultValues={selectedMatiere || {}} onSubmit={handleSave}>
          <ValidatedField label={translate('korischoolApp.matiere.nom')} id="matiere-create-nom" name="nom" data-cy="nom" type="text" />
          <ValidatedField
            label={translate('korischoolApp.matiere.color')}
            id="matiere-create-color"
            name="color"
            data-cy="color"
            type="color"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeFormModal} disabled={updating}>
              Annuler
            </Button>
            <Button htmlType="submit" loading={updating} style={{ backgroundColor: '#F28E19', color: 'white' }}>
              Enregistrer
            </Button>
          </div>
        </ValidatedForm>
      </Modal>
    </Card>
  );
};

export default MatiereEcole;
